import {useState} from "react";
import type {Report} from "../../../data/models/Report";
import {useAppStore} from "../../../StateManager";
import {Permission, XyiconFeature} from "../../../generated/api/base";
import type {User} from "../../../data/models/User";
import type {UserGroup} from "../../../data/models/UserGroup";
import type {ReportSharingSettingsData} from "../../../generated/api/reports";
import {getLabelFromViewReportOrViewFolder} from "./SharingUtils";
import {SharingPopupV5} from "./SharingPopupV5";
import SharingContentCommon from "./SharingContentCommon";

interface IReportSharingContainer {
	readonly report: Report;
	readonly onClose?: () => void;
}

const ReportSharingContainer = ({report, onClose}: IReportSharingContainer) => {
	const appState = useAppStore((state) => state.appState);
	const [_, setRefreshKey] = useState(0);

	const isPopup = !!onClose;

	const onSaveChanges = async () => {
		const {result} = await appState.actions.updateReport(report);

		// manual re-render to sync the mutated report data
		setRefreshKey((prevKey) => prevKey + 1);

		return result;
	};

	const onAddClick = (id: string, permission: Permission.View | Permission.Update, feature: XyiconFeature.User | XyiconFeature.UserGroup) => {
		const item = appState.actions.getFeatureItemById<User | UserGroup>(id, feature);
		const sharingSettings = report.sharingSettings || [];
		const administratorUserGroupId = appState.organization?.settings?.userGroups?.administrators;
		const isAdministratorUserGroup =
			feature === XyiconFeature.UserGroup && (item.id === administratorUserGroupId || (item as UserGroup).name === "Administrators");
		const permissionType = report.isGlobal ? isAdministratorUserGroup : permission === Permission.Update;

		if (feature === XyiconFeature.User) {
			sharingSettings.push({
				userID: item.id,
				userGroupID: null,
				canEditSharedReport: permissionType,
			});
		} else if (feature === XyiconFeature.UserGroup) {
			sharingSettings.push({
				userID: null,
				userGroupID: item.id,
				canEditSharedReport: permissionType,
			});
		}
	};

	const onDeleteUserGroup = (userGroup: UserGroup) => {
		report.removeUserGroupFromSharing(userGroup.id);

		return onSaveChanges();
	};

	const onDeleteUser = (user: User) => {
		report.removeUserFromSharing(user.id);

		return onSaveChanges();
	};

	const onConfirmOwnershipChange = (sharing: ReportSharingSettingsData) => {
		const tempOwnerId = report.ownedBy;

		// set new report owner
		report.setOwnerUserId(sharing.userID);

		// set editing permission to old owner
		const oldOwner = report.sharingSettings.find((sharing) => sharing.userID === tempOwnerId);

		if (oldOwner) {
			oldOwner.canEditSharedReport = true;
		} else {
			const tempOwner = appState.actions.findUser(tempOwnerId);

			report.sharingSettings.push({
				userID: tempOwner.id,
				userGroupID: null,
				canEditSharedReport: true,
			});
		}
	};

	const sharingProps = {
		onAddClick,
		onDeleteUserGroup,
		onDeleteUser,
		onConfirmOwnershipChange,
		onSaveChanges,
		onClose,
		ownedByUserId: report.ownedBy,
		itemLabel: getLabelFromViewReportOrViewFolder(report),
		itemName: report.name,
		sharingSettings: report.sharingSettings,
		showShareOption: true,
		isGlobal: report.isGlobal,
	};

	return isPopup ? <SharingPopupV5 {...sharingProps} /> : <SharingContentCommon {...sharingProps} />;
};

export default ReportSharingContainer;
